import { Formik, FormikProps } from "formik";
import { useCallback, useEffect } from "react";
import {
  IExtendTrialFormValues,
  extendTrialValidationSchema,
  getExtendTrialFormInitialValues,
} from "../../../../common/validations/extend-trial.validations";

interface ExtendTrialFormHandlers {
  handleDaysChange: (value: string) => void;
  handleReasonChange: (value: string) => void;
}

export interface ExtendTrialFormChildrenProps
  extends FormikProps<IExtendTrialFormValues>,
    ExtendTrialFormHandlers {}

interface ExtendTrialFormWrapperProps {
  initialValues?: IExtendTrialFormValues;
  onFormStateChange?: (values: IExtendTrialFormValues, isValid: boolean) => void;
  onSubmit: (values: IExtendTrialFormValues) => void;
  children: (props: ExtendTrialFormChildrenProps) => React.ReactNode;
}

interface FormContentProps {
  formikProps: FormikProps<IExtendTrialFormValues>;
  onFormStateChange?: (values: IExtendTrialFormValues, isValid: boolean) => void;
  onSubmit: (values: IExtendTrialFormValues) => void;
  children: (props: ExtendTrialFormChildrenProps) => React.ReactNode;
}

const FormContent = ({
  formikProps,
  onFormStateChange,
  onSubmit,
  children,
}: FormContentProps) => {
  const { values, isValid, setFieldValue } = formikProps;

  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(values, isValid);
    }
  }, [values, isValid, onFormStateChange]);

  const handleDaysChange = useCallback(
    (value: string) => {
      setFieldValue("days", value);
    },
    [setFieldValue]
  );

  const handleReasonChange = useCallback(
    (value: string) => {
      setFieldValue("reason", value);
    },
    [setFieldValue]
  );

  return (
    <>
      {children({
        ...formikProps,
        handleDaysChange,
        handleReasonChange,
        handleSubmit: (e) => {
          e?.preventDefault();
          onSubmit(values);
        },
      })}
    </>
  );
};

const ExtendTrialFormWrapper = ({
  initialValues = getExtendTrialFormInitialValues(),
  onFormStateChange,
  onSubmit,
  children,
}: ExtendTrialFormWrapperProps) => {
  const handleSubmit = (values: IExtendTrialFormValues) => {
    onSubmit(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={extendTrialValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validateOnMount={true}
    >
      {(formikProps) => (
        <FormContent
          formikProps={formikProps}
          onFormStateChange={onFormStateChange}
          onSubmit={onSubmit}
          children={children}
        />
      )}
    </Formik>
  );
};

export default ExtendTrialFormWrapper;

